import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Page, Typography } from 'seagull';
import uniqBy from 'lodash/uniqBy';
import { useIoContext, useStoreActions, useStoreStatic } from "../../contexts";
import { routes } from "../../constants";
import ImportWizard from "../../components/ImportWizard/ImportWizard";
import { useServiceTemplateApi } from "../../hooks/useServiceTemplateApi";
import { selectors } from "../../store/state-selectors";
import { parseAndValidateAgreementCsv } from "./parseAndValidateAgreementCsv";
import { serviceModel, serviceTemplateModel } from "../../models";
import { helpArticles } from "../../utils/helpArticles";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import { ErrorResponse } from "../../infra/ErrorResponse";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useAppConfig } from "../../store/store-context";
const title = 'Import agreements';
const servicesEntity = 'service templates';
const contactsEntity = 'contacts';
const oldCsvTemplateUrl = 'https://storage.googleapis.com/anchor-statics/Anchor_Bulk_Import-2024-11-11.zip';
const csvTemplateUrl = 'https://storage.googleapis.com/anchor-statics/Anchor_Bulk_Import-2025-01-11.zip';
const getServiceUniqueKey = s => s.name;

/** Imports agreements from a CSV file and saves them as drafts. */
export default function ImportAgreements() {
  const history = useHistory();
  const {
    logBI,
    api
  } = useStoreActions();
  const {
    getState
  } = useStoreStatic();
  const {
    getNow
  } = useIoContext();
  const serviceTemplateApi = useServiceTemplateApi();
  const [contacts, setContacts] = useState([]);
  const [currentSaveData, setCurrentSaveData] = useState();
  const {
    PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD,
    NEW_BILLING_DAY
  } = useFeatureFlags();
  const {
    generateUniqueId
  } = useAppConfig();
  useEffect(() => {
    document.title = title;
  }, []);
  const onStartSave = async items => {
    const drafts = items.map(i => i.draft);
    const newContacts = items.map(i => i.contact);
    const [existingContacts, existingServiceTemplates] = await Promise.all([api.contact.getAll(), serviceTemplateApi.get()]);
    setContacts(existingContacts);
    const newServices = [];
    drafts.forEach(d => {
      var _d$serviceBundles;
      d.services.forEach(s => {
        const service = serviceModel.fromDTO(s);
        if (service != null) newServices.push(service);
      });
      (_d$serviceBundles = d.serviceBundles) === null || _d$serviceBundles === void 0 || _d$serviceBundles.bundles.forEach(_ref => {
        let {
          services
        } = _ref;
        return services.forEach(s => {
          const service = serviceModel.fromDTO(s);
          if (service != null) newServices.push(service);
        });
      });
    });
    const uniqueNewServices = uniqBy(newServices, getServiceUniqueKey);
    const uniqueNewContacts = uniqBy(newContacts, c => c.uniqueKey());

    // Create new service-templates if needed.
    const existingServiceTemplatesUniqueKeys = new Set(existingServiceTemplates.raw.map(getServiceUniqueKey));
    const servicesToSave = uniqueNewServices.filter(s => {
      return !existingServiceTemplatesUniqueKeys.has(getServiceUniqueKey(s));
    });
    let savedServices = 0;
    const setServicesSaveData = () => setCurrentSaveData({
      entity: servicesEntity,
      saved: savedServices,
      total: servicesToSave.length
    });
    setServicesSaveData();
    for (const s of servicesToSave) {
      const template = serviceTemplateModel.fromService(s);
      await serviceTemplateApi.create(serviceTemplateModel.makeServiceTemplate({
        id: template.id,
        name: template.name,
        description: template.description,
        cost: template.cost,
        billing: template.billing
      }));
      savedServices++;
      setServicesSaveData();
    }

    // Create new contacts if needed.
    const existingContactsUniqueKeys = new Set(existingContacts.map(c => c.uniqueKey()));
    const contactsToSave = uniqueNewContacts.filter(c => !existingContactsUniqueKeys.has(c.uniqueKey()));
    let savedContacts = 0;
    const setContactsSaveData = errorMsg => setCurrentSaveData({
      entity: contactsEntity,
      saved: savedContacts,
      total: contactsToSave.length,
      errorMsg
    });
    setContactsSaveData();
    for (const c of contactsToSave) {
      await api.contact.create(c).catch(err => {
        const msg = new ErrorResponse(err).saveContactErrorText();
        setContactsSaveData(msg);
        throw err;
      });
      savedContacts++;
      setContactsSaveData();
    }

    // Refresh contacts to attach them later to the saved drafts.
    if (contactsToSave.length) {
      const newContactsRes = await api.contact.getAll();
      setContacts(newContactsRes);
    }
    setCurrentSaveData(undefined);
  };
  return /*#__PURE__*/React.createElement(Page, {
    skin: "secondary",
    header: title,
    subheader: /*#__PURE__*/React.createElement(Typography, null, "Import your existing customers in bulk in a few simple steps.", ' ', /*#__PURE__*/React.createElement(ExternalLinkButton, {
      openInNewTab: true,
      underline: "none",
      href: helpArticles.importClientsBulk()
    }, "Click here"), ' ', "to learn more about the process.")
  }, /*#__PURE__*/React.createElement(ImportWizard, {
    entityName: "agreements",
    saveEntityName: "drafts",
    currentSaveData: currentSaveData,
    importBtnText: "Import as drafts",
    csvTemplateUrl: PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD ? csvTemplateUrl : oldCsvTemplateUrl,
    testid: "import-agreements",
    onStartSave: onStartSave,
    saveItem: _ref2 => {
      let {
        draft,
        contact: newContact
      } = _ref2;
      const contactUniqueKeyFromDraft = newContact.uniqueKey();
      const contact = contacts.find(c => c.uniqueKey() === contactUniqueKeyFromDraft);
      draft.clientContactId = contact ? contact.id() : null;
      return api.draft.save(draft);
    },
    parseAndValidateCsv: async data => {
      var _selectors$user$busin;
      logBI(10901);
      const libraryLegalTermsId = (_selectors$user$busin = selectors.user.businessSettings(getState())) === null || _selectors$user$busin === void 0 ? void 0 : _selectors$user$busin.legalTermsDocumentId;
      const legalTerms = libraryLegalTermsId ? {
        type: 'libraryLegalTermsId',
        libraryLegalTermsId
      } : null;
      const availableLegalTerms = await api.business.getLegalTerms();
      const {
        items,
        errors
      } = await parseAndValidateAgreementCsv({
        data,
        defaultDraftsValues: {
          legalTerms
        },
        availableLegalTerms,
        now: getNow(),
        PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD,
        NEW_BILLING_DAY,
        generateUniqueId
      });
      logBI(10902, {
        numAgreements: items.length,
        numErrors: errors.length
      });
      return {
        items,
        errors
      };
    },
    onEvent: name => {
      if (name === 'csvTemplateDownload') {
        logBI(10900);
      } else if (name === 'startImportItems') {
        logBI(10903);
      } else if (name === 'redirectToItems') {
        history.push(routes.draftsDashboard);
      } else if (name === 'afterSaveFailure') {
        logBI(10908);
      } else if (name === 'afterSaveSuccess') {
        logBI(10909);
      }
    }
  }));
}